import { useState, useEffect } from "react";
import { useTerminology } from '@/hooks/useTerminology';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, differenceInDays } from "date-fns";
import { useFormatting } from "@/hooks/useFormatting";

interface Invoice {
  id: string;
  number: string;
  customer: string;
  amount: number;
  dueDate: Date;
}

interface DelayInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddAdjustment: (adjustment: {
    type: "delay_invoice";
    invoiceId: string;
    invoiceNumber: string;
    customer: string;
    amount: number;
    originalDueDate: Date;
    newDueDate: Date;
    reason?: string;
  }) => void;
}

export function DelayInvoiceModal({ open, onOpenChange, onAddAdjustment }: DelayInvoiceModalProps) {
  const { getCashMovementTerms } = useTerminology();
  const { formatCurrency } = useFormatting();
  // Mock invoice data - in real app, this would come from API
  const invoices: Invoice[] = [
    {
      id: "inv-1234",
      number: "1234",
      customer: "BigCo Ltd",
      amount: 22000,
      dueDate: new Date(2025, 10, 23), // Nov 23, 2025
    },
    {
      id: "inv-1235",
      number: "1235",
      customer: "SmallCo",
      amount: 18500,
      dueDate: new Date(2025, 10, 30), // Nov 30, 2025
    },
    {
      id: "inv-1240",
      number: "1240",
      customer: "MedCo Inc",
      amount: 15200,
      dueDate: new Date(2025, 11, 5), // Dec 5, 2025
    },
  ];

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
  const [newDueDate, setNewDueDate] = useState<Date | undefined>(undefined);
  const [reason, setReason] = useState("");

  const selectedInvoice = invoices.find((inv) => inv.id === selectedInvoiceId);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedInvoiceId("");
      setNewDueDate(undefined);
      setReason("");
    }
  }, [open]);

  const handleAddAdjustment = () => {
    if (!selectedInvoice || !newDueDate) return;

    onAddAdjustment({
      type: "delay_invoice",
      invoiceId: selectedInvoice.id,
      invoiceNumber: selectedInvoice.number,
      customer: selectedInvoice.customer,
      amount: selectedInvoice.amount,
      originalDueDate: selectedInvoice.dueDate,
      newDueDate,
      reason: reason || undefined,
    });

    onOpenChange(false);
  };

  const daysDelayed = selectedInvoice && newDueDate
    ? differenceInDays(newDueDate, selectedInvoice.dueDate)
    : 0;

  const isValid = selectedInvoiceId && newDueDate && newDueDate > (selectedInvoice?.dueDate || new Date());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Delay Invoice Payment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Select Invoice */}
          <div className="space-y-2">
            <Label htmlFor="invoice">
              Select Invoice <span className="text-red-500">*</span>
            </Label>
            <Select value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
              <SelectTrigger id="invoice">
                <SelectValue placeholder="Search invoices..." />
              </SelectTrigger>
              <SelectContent>
                {invoices.map((invoice) => (
                  <SelectItem key={invoice.id} value={invoice.id}>
                    Invoice #{invoice.number} - {invoice.customer} - £
                    {formatCurrency(invoice.amount)} (Due {format(invoice.dueDate, "MMM d")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Current Due Date (auto-filled) */}
          {selectedInvoice && (
            <div className="space-y-2">
              <Label>Current Due Date</Label>
              <Input
                value={format(selectedInvoice.dueDate, "MMM d, yyyy")}
                disabled
                className="bg-muted"
              />
            </div>
          )}

          {/* New Due Date */}
          <div className="space-y-2">
            <Label htmlFor="new-due-date">
              New Due Date <span className="text-red-500">*</span>
            </Label>
            <DatePicker
              id="new-due-date"
              value={newDueDate}
              onChange={setNewDueDate}
              dateFormat="PPP"
              placeholder="Pick a date"
              disabledDate={(date) =>
                selectedInvoice ? date <= selectedInvoice.dueDate : date < new Date()
              }
            />
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Customer requested extension"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>

          {/* Impact Preview */}
          {selectedInvoice && newDueDate && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
              <div className="font-medium text-sm">Impact Preview</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Cash impact on {format(selectedInvoice.dueDate, "MMM d")}:</span>
                  <span className="text-red-600 font-medium">
                    -{formatCurrency(selectedInvoice.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Cash impact on {format(newDueDate, "MMM d")}:</span>
                  <span className="text-green-600 font-medium">
                    +{formatCurrency(selectedInvoice.amount)}
                  </span>
                </div>
                <div className="pt-2 border-t mt-2">
                  <span className="text-muted-foreground">
                    Net effect: Delays £{(selectedInvoice.amount / 1000).toFixed(0)}K {getCashMovementTerms().in.toLowerCase()} by{" "}
                    {daysDelayed} days
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddAdjustment} disabled={!isValid}>
            Add Adjustment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
